/**
 * useWaInboxRealtime — canal Supabase Realtime del inbox WhatsApp.
 *
 * Centraliza los 2 canales que WhatsAppInboxPage necesita en todo momento:
 *   1. postgres_changes sobre wa_conversations + wa_messages (tenant-scoped).
 *   2. broadcast de presencia (composing/recording/available desde Evolution API).
 *
 * El canal de agent-typing permanece en la página porque varía con selectedConvId
 * y es menos crítico.
 *
 * Todos los setters y refs reciben sus valores del caller — el hook NO posee estado.
 */

import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { playReceiveSound } from '@/lib/whatsappSounds';
import { bestDeliveryStatus, convMatchesFilter, isRenderableMessage } from '@/lib/wa-utils';
import { waDisplayName } from '@/components/whatsapp/wa-helpers';
import type { WaConversation } from '@/hooks/useWaConversations';
import type { WaMessage } from '@/components/whatsapp/WaMessageThread';
import { aplicarAlHilo, bajarScrollDelHilo, hayHiloAbierto } from '@/hooks/waThreadRegistry';

type Conversation = WaConversation;
type Message = WaMessage;

interface Options {
  tenantId: string | null;
  statusFilterRef: React.MutableRefObject<string>;
  loadConversationsRef: React.MutableRefObject<(() => Promise<void>) | null>;
  conversationsRef: React.MutableRefObject<WaConversation[]>;
  setConversations: React.Dispatch<React.SetStateAction<WaConversation[]>>;
  setPresenceMap: React.Dispatch<React.SetStateAction<Map<string, 'composing' | 'recording' | 'available'>>>;
  /**
   * La conversación desapareció de la vista (la borraron, o un cambio de estado la sacó del filtro
   * activo). Solo le importa a quien tenga una seleccionada — la bandeja.
   */
  onConversacionFuera?: (convId: string) => void;
  /**
   * Aviso de mensaje entrante en una conversación que NO está abierta en pantalla. Opcional: si
   * nadie lo registra, no se avisa. El aviso es una decisión de la pantalla, no del canal.
   */
  showToast?: (content: import('./useInboxToast').ToastContent) => void;
}

export function useWaInboxRealtime({
  tenantId,
  statusFilterRef,
  loadConversationsRef,
  conversationsRef,
  setConversations,
  setPresenceMap,
  onConversacionFuera,
  showToast,
}: Options): void {
  useEffect(() => {
    if (!tenantId) return;

    const channel = supabase
      .channel(`wa-inbox-${tenantId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'wa_conversations', filter: `tenant_id=eq.${tenantId}` },
        (payload) => {
          // Inline broadcast: usar payload.new directo en lugar de refetch
          // completo. Ahorra 1 roundtrip + 200ms en cada conversación nueva.
          // Solo caemos a refetch si filter activo descarta este status
          // (no tiene sentido mostrar lo que el filter oculta).
          const u = payload.new as Record<string, unknown>;
          const id = u.id as string;
          const status = (u.status as string) ?? 'pending';
          if (!convMatchesFilter(status, statusFilterRef.current)) return;

          setConversations((prev) => {
            // Idempotente: si ya existe (race con realtime + refetch inicial), skip.
            if (prev.some((c) => c.id === id)) return prev;
            const newConv = {
              ...u,
              tags: Array.isArray(u.tags) ? u.tags : [],
              unread_count: (u.unread_count as number) ?? 0,
              message_count: (u.message_count as number) ?? 0,
            } as unknown as Conversation;
            // Bump al top: la conv recién creada va primera por last_message_at.
            let result = [newConv, ...prev].sort((a, b) => {
              const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
              const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
              return tb - ta;
            });
            // 🔧 Deduplicar por si acaso: realtime puede emitir INSERT fantasma
            const seen = new Set<string>();
            result = result.filter((c) => {
              if (seen.has(c.id)) return false;
              seen.add(c.id);
              return true;
            });
            return result;
          });
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'wa_conversations', filter: `tenant_id=eq.${tenantId}` },
        (payload) => {
          const u = payload.new as Record<string, unknown>;
          const id = u.id as string;
          setConversations((prev) => {
            const exists = prev.some((c) => c.id === id);
            if (!exists) { void loadConversationsRef.current?.(); return prev; }

            const newStatus = u.status as string | undefined;
            if (newStatus && !convMatchesFilter(newStatus, statusFilterRef.current)) {
              onConversacionFuera?.(id);
              return prev.filter((c) => c.id !== id);
            }

            const patched = prev.map((c) => {
              if (c.id !== id) return c;
              return {
                ...c,
                ...(u.contact_name !== undefined && { contact_name: u.contact_name as string | null }),
                ...(u.contact_photo_url !== undefined && { contact_photo_url: u.contact_photo_url as string | null }),
                ...(u.status !== undefined && { status: u.status as string }),
                ...(u.assigned_agent_id !== undefined && { assigned_agent_id: u.assigned_agent_id as string | null }),
                ...(u.team_id !== undefined && { team_id: u.team_id as string | null }),
                ...(u.priority !== undefined && { priority: u.priority as string | null }),
                ...(u.ai_handling !== undefined && { ai_handling: u.ai_handling as boolean }),
                ...(u.unread_count !== undefined && { unread_count: u.unread_count as number }),
                ...(u.message_count !== undefined && { message_count: u.message_count as number }),
                ...(u.last_message_at !== undefined && { last_message_at: u.last_message_at as string | null }),
                ...(u.last_message_body !== undefined && { last_message_body: u.last_message_body as string | null }),
                ...(u.last_message_type !== undefined && { last_message_type: u.last_message_type as string | null }),
                ...(u.last_message_direction !== undefined && { last_message_direction: u.last_message_direction as string | null }),
                // Anti-degradación: si ya teníamos un status superior (read)
                // y llega UPDATE con uno inferior (delivered) por race del
                // realtime out-of-order, conservamos el mayor. Mismo guard
                // que el backend processStatusUpdate, replicado en cliente.
                ...(u.last_message_status !== undefined && {
                  last_message_status: bestDeliveryStatus(
                    c.last_message_status ?? '',
                    (u.last_message_status as string | null) ?? '',
                  ) || (u.last_message_status as string | null),
                }),
                ...(u.tags !== undefined && { tags: (u.tags as string[]) ?? c.tags }),
                ...(u.is_pinned !== undefined && { is_pinned: u.is_pinned as boolean }),
                ...(u.muted_until !== undefined && { muted_until: u.muted_until as string | null }),
                ...(u.snoozed_until !== undefined && { snoozed_until: u.snoozed_until as string | null }),
                ...(u.crm_lead_id !== undefined && { crm_lead_id: u.crm_lead_id as string | null }),
                ...(u.sla_first_response_breached !== undefined && { sla_first_response_breached: u.sla_first_response_breached as boolean }),
                ...(u.sla_resolution_breached !== undefined && { sla_resolution_breached: u.sla_resolution_breached as boolean }),
              } as Conversation;
            });
            // Re-ordenar si el UPDATE cambia last_message_at. El RPC
            // wa_send_prepare hace UPDATE wa_conversations + INSERT
            // wa_messages; si el UPDATE llega primero y no reordenamos,
            // la conv queda con texto nuevo pero en posición vieja
            // hasta el INSERT (race visible en chats activos).
            let result = patched;
            if (u.last_message_at !== undefined) {
              result = [...patched].sort((a, b) => {
                const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
                const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
                return tb - ta;
              });
            }
            // 🔧 Deduplicar por si acaso: realtime UPDATE puede causar fantasmas
            const seen = new Set<string>();
            result = result.filter((c) => {
              if (seen.has(c.id)) return false;
              seen.add(c.id);
              return true;
            });
            return result;
          });
        },
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'wa_conversations', filter: `tenant_id=eq.${tenantId}` },
        (payload) => {
          // Inline: filtrar por id en lugar de refetch.
          const id = (payload.old as { id?: string })?.id;
          if (!id) return;
          setConversations((prev) => prev.filter((c) => c.id !== id));
          onConversacionFuera?.(id);
        },
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'wa_messages', filter: `tenant_id=eq.${tenantId}` },
        (payload) => {
          const rawMsg = payload.new as Record<string, unknown> & { conversation_id: string };
          const msg = {
            ...rawMsg,
            reactions: Array.isArray(rawMsg.reactions) ? rawMsg.reactions : [],
            wa_message_id: (rawMsg.wa_message_id as string) ?? null,
          } as Message & { conversation_id: string };

          // Fantasmas (reactions coladas como mensaje, bubbles vacíos):
          // ignorar todo el INSERT — no entran al thread ni patchean
          // last_message_*/unread de la conv. WA Web nunca los muestra.
          if (!isRenderableMessage(msg)) return;

          if (hayHiloAbierto(msg.conversation_id)) {
            aplicarAlHilo(msg.conversation_id, (prev) => {
              if (prev.some((m) => m.id === msg.id || (msg.wa_message_id && m.wa_message_id === msg.wa_message_id))) {
                return prev;
              }
              if (msg.direction === 'outbound') {
                // matchTemp dedupea optimistic temp message contra realtime
                // INSERT del mismo envío. 3 estrategias en orden:
                // 1. body match (texto puro)
                // 2. media_url match (storage URL después del upload)
                // 3. msg_type + recent (~10s) match para media sin caption
                //    cuyos URLs no coinciden (blob://temp vs https://storage)
                const RECENT_MS = 10_000;
                const msgCreatedAt = new Date(msg.created_at).getTime();
                const matchTemp = (m: Message) => {
                  if (!m.id.startsWith('temp-')) return false;
                  // Strategy 1: body match (texto)
                  if (m.body && m.body === msg.body) return true;
                  // Strategy 2: media_url exact match
                  if (m.media_url && m.media_url === msg.media_url) return true;
                  // Strategy 3: misma msg_type media sin caption + creado dentro
                  // de 10s — cubre el race donde realtime llega ANTES de que el
                  // upload local complete su URL pública (blob:// vs https://).
                  if (m.msg_type === msg.msg_type
                      && msg.msg_type !== 'text'
                      && !m.body && !msg.body) {
                    const tempCreatedAt = new Date(m.created_at).getTime();
                    if (Math.abs(msgCreatedAt - tempCreatedAt) < RECENT_MS) return true;
                  }
                  return false;
                };
                const hasTemp = prev.some(matchTemp);
                if (hasTemp) {
                  let replaced = false;
                  return prev.map((m) => {
                    if (!replaced && matchTemp(m)) {
                      replaced = true;
                      const bestStatus = bestDeliveryStatus(m.delivery_status, msg.delivery_status);
                      return {
                        ...msg,
                        delivery_status: bestStatus,
                        ...(m.quoted_message ? { quoted_message: m.quoted_message, quoted_message_id: m.quoted_message_id } : {}),
                      };
                    }
                    return m;
                  });
                }
              }
              if (msg.direction === 'inbound') playReceiveSound();
              return [...prev, msg];
            });
            bajarScrollDelHilo(msg.conversation_id);
          } else if (msg.direction === 'inbound') {
            playReceiveSound();
            const conv = conversationsRef.current.find((c: Conversation) => c.id === rawMsg.conversation_id);
            const cj = (conv as { contact_jid?: string | null } | undefined)?.contact_jid ?? null;
            showToast?.({
              conversationId: rawMsg.conversation_id,
              name: conv ? waDisplayName(conv.contact_name, conv.contact_phone, cj) : 'Nuevo mensaje',
              body: (rawMsg.body as string)?.slice(0, 80) || '📎 Archivo',
              phone: conv?.contact_phone ?? '',
              photoUrl: (conv as { contact_photo_url?: string | null } | undefined)?.contact_photo_url ?? null,
              contactJid: cj,
            });
          }

          setConversations((prev) => {
            // Patch + bump al top: la conversation que recibió el mensaje
            // se re-ordena para quedar primera (patrón WhatsApp Web/Slack).
            // Sin esto la lista solo se reordenaba al refrescar la página.
            const updated = prev.map((c) => {
              if (c.id !== rawMsg.conversation_id) return c;
              return {
                ...c,
                last_message_body: (rawMsg.body as string) ?? null,
                last_message_type: (rawMsg.msg_type as string) ?? 'text',
                last_message_direction: (rawMsg.direction as string) ?? 'inbound',
                last_message_status: (rawMsg.delivery_status as string) ?? 'sent',
                last_message_at: (rawMsg.created_at as string) ?? new Date().toISOString(),
                // Si el hilo está en pantalla (bandeja o ventana acoplada) el mensaje ya se está viendo.
                unread_count: hayHiloAbierto(rawMsg.conversation_id) ? c.unread_count : c.unread_count + 1,
                typing: false,
              };
            });
            return [...updated].sort((a, b) => {
              const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
              const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
              return tb - ta;
            });
          });
        },
      )
      .on(
        'broadcast',
        { event: 'wa_message_insert' },
        (payload) => {
          // Server-side broadcast: el webhook publica el mensaje inbound al
          // canal Realtime ANTES de que postgres logical replication lo
          // empuje vía postgres_changes. Llega ~100-200ms antes.
          //
          // Dedup: el INSERT handler de postgres_changes va a llegar después
          // con el mismo mensaje. Filtramos por id en setMessages.
          const rawMsg = payload.payload as Record<string, unknown> & { conversation_id: string };
          if (!rawMsg?.id) return;
          const msg = {
            ...rawMsg,
            reactions: Array.isArray(rawMsg.reactions) ? rawMsg.reactions : [],
            wa_message_id: (rawMsg.wa_message_id as string) ?? null,
          } as Message & { conversation_id: string };

          // Mismo guard de fantasmas que el INSERT postgres_changes.
          if (!isRenderableMessage(msg)) return;

          if (hayHiloAbierto(msg.conversation_id)) {
            aplicarAlHilo(msg.conversation_id, (prev) => {
              if (prev.some((m) => m.id === msg.id || (msg.wa_message_id && m.wa_message_id === msg.wa_message_id))) {
                return prev;
              }
              if (msg.direction === 'inbound') playReceiveSound();
              return [...prev, msg];
            });
            bajarScrollDelHilo(msg.conversation_id);
          } else if (msg.direction === 'inbound') {
            playReceiveSound();
            const conv = conversationsRef.current.find((c: Conversation) => c.id === rawMsg.conversation_id);
            const cj = (conv as { contact_jid?: string | null } | undefined)?.contact_jid ?? null;
            showToast?.({
              conversationId: rawMsg.conversation_id,
              name: conv ? waDisplayName(conv.contact_name, conv.contact_phone, cj) : 'Nuevo mensaje',
              body: (rawMsg.body as string)?.slice(0, 80) || '📎 Archivo',
              phone: conv?.contact_phone ?? '',
              photoUrl: (conv as { contact_photo_url?: string | null } | undefined)?.contact_photo_url ?? null,
              contactJid: cj,
            });
          }

          // Bump conversation al top en la lista
          setConversations((prev) => {
            const updated = prev.map((c) => {
              if (c.id !== rawMsg.conversation_id) return c;
              return {
                ...c,
                last_message_body: (rawMsg.body as string) ?? null,
                last_message_type: (rawMsg.msg_type as string) ?? 'text',
                last_message_direction: (rawMsg.direction as string) ?? 'inbound',
                last_message_status: (rawMsg.delivery_status as string) ?? 'delivered',
                last_message_at: (rawMsg.created_at as string) ?? new Date().toISOString(),
                // Si el hilo está en pantalla (bandeja o ventana acoplada) el mensaje ya se está viendo.
                unread_count: hayHiloAbierto(rawMsg.conversation_id) ? c.unread_count : c.unread_count + 1,
                typing: false,
              };
            });
            return [...updated].sort((a, b) => {
              const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
              const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
              return tb - ta;
            });
          });
        },
      )
      .on(
        'broadcast',
        { event: 'wa_message_status' },
        (payload) => {
          const p = payload.payload as { message_id: string; conversation_id: string; delivery_status: string; is_latest_in_conv: boolean };
          if (!p?.message_id) return;

          if (hayHiloAbierto(p.conversation_id)) {
            aplicarAlHilo(p.conversation_id, (prev) => prev.map((m) => {
              if (m.id !== p.message_id) return m;
              return { ...m, delivery_status: bestDeliveryStatus(m.delivery_status, p.delivery_status) };
            }));
          }

          if (p.is_latest_in_conv) {
            setConversations((prev) => prev.map((c) => {
              if (c.id !== p.conversation_id || c.last_message_direction !== 'outbound') return c;
              const next = bestDeliveryStatus(c.last_message_status ?? '', p.delivery_status);
              if (next !== c.last_message_status) return { ...c, last_message_status: next };
              return c;
            }));
          }
        },
      )
      .on(
        'broadcast',
        { event: 'wa_message_reaction' },
        (payload) => {
          // INSTANT broadcast de emoji reactions. Mismo dedup que status:
          // el postgres_changes UPDATE llegará después con el mismo reactions[]
          // y el merge será idempotente.
          const p = payload.payload as { message_id: string; conversation_id: string; reactions: Array<{ emoji: string; from: string; from_me: boolean; timestamp: number }> };
          if (!p?.message_id) return;
          aplicarAlHilo(p.conversation_id, (prev) => prev.map((m) => {
            if (m.id !== p.message_id) return m;
            return { ...m, reactions: p.reactions };
          }));
        },
      )
      .on(
        'broadcast',
        { event: 'wa_message_edit' },
        (payload) => {
          // INSTANT broadcast de edit inbound (Feature 3 Fase B).
          // Cuando el contacto edita un mensaje, el webhook hace UPDATE
          // del wa_messages.body + is_edited + edited_at, y broadcast
          // este evento para que el frontend refleje el cambio sin
          // esperar postgres_changes (~50ms vs ~300ms).
          const p = payload.payload as { message_id: string; conversation_id: string; new_body: string; edited_at: string };
          if (!p?.message_id) return;
          aplicarAlHilo(p.conversation_id, (prev) => prev.map((m) => {
            if (m.id !== p.message_id) return m;
            return {
              ...m,
              body: p.new_body,
              is_edited: true,
              edited_at: p.edited_at,
            } as typeof m;
          }));
        },
      )
      .on(
        'broadcast',
        { event: 'wa_message_delete' },
        (payload) => {
          // INSTANT broadcast de revoke ("Eliminar para todos") inbound.
          // El webhook setea deleted_at; el bubble renderiza el estado
          // "🚫 Este mensaje fue eliminado" sin esperar postgres_changes.
          const p = payload.payload as { message_id: string; conversation_id: string; deleted_at: string };
          if (!p?.message_id) return;
          aplicarAlHilo(p.conversation_id, (prev) => prev.map((m) => {
            if (m.id !== p.message_id) return m;
            return { ...m, deleted_at: p.deleted_at } as typeof m;
          }));
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'wa_messages', filter: `tenant_id=eq.${tenantId}` },
        (payload) => {
          const raw = payload.new as Record<string, unknown> & { conversation_id: string };
          const updated = {
            ...raw,
            reactions: Array.isArray(raw.reactions) ? raw.reactions : [],
            wa_message_id: (raw.wa_message_id as string) ?? null,
          } as Message & { conversation_id: string };

          if (hayHiloAbierto(updated.conversation_id)) {
            aplicarAlHilo(updated.conversation_id, (prev) => prev.map((m) => {
              if (m.id !== updated.id) return m;
              return {
                ...m,
                delivery_status: bestDeliveryStatus(m.delivery_status, updated.delivery_status),
                reactions: updated.reactions,
                media_url: updated.media_url ?? m.media_url,
                // media_status y media_thumbnail también se propagan: el
                // background download del webhook actualiza el row con
                // media_url + media_status='in_r2'. Sin propagar status,
                // el frontend seguía viendo 'pending' aunque ya tuviera URL.
                ...(updated.media_status !== undefined ? { media_status: updated.media_status } : {}),
                ...(updated.media_thumbnail !== undefined ? { media_thumbnail: updated.media_thumbnail } : {}),
                // Feature 5c-B: el pre-fetch eager de orderDetails actualiza
                // order_info.items DESPUÉS del INSERT. Sin propagar acá, la
                // card no muestra la imagen del producto hasta refrescar.
                ...(updated.order_info !== undefined ? { order_info: updated.order_info } : {}),
                // Revoke "Eliminar para todos": propagación postgres_changes
                // como fallback del broadcast wa_message_delete.
                ...(updated.deleted_at !== undefined ? { deleted_at: updated.deleted_at as string | null } : {}),
              };
            }));
          }

          if (updated.direction === 'outbound') {
            setConversations((prev) => prev.map((c) => {
              if (c.id !== updated.conversation_id || c.last_message_direction !== 'outbound') return c;
              const next = bestDeliveryStatus(c.last_message_status ?? '', updated.delivery_status);
              if (next !== c.last_message_status) return { ...c, last_message_status: next };
              return c;
            }));
          }
        },
      )
      .subscribe();

    // Presencia — modelo WhatsApp Web (NO timers de expiración para
    // 'available'): WhatsApp manda 'available' UNA vez al entrar en línea
    // y 'unavailable' al salir; entre medio NO repite nada. Por eso
    // 'available' es STICKY hasta que llegue 'unavailable' (expirarlo a
    // los 60s mostraba "últ. vez" falsa mientras el contacto seguía
    // online sin escribir). composing/recording son overlays transitorios
    // que, al terminar, vuelven a 'available' (sigue online), NO a offline.
    // Timestamp del último evento por teléfono (scope del effect, persiste
    // entre eventos mientras la subscripción vive). Sirve para el TTL de
    // seguridad reseteable: un timer viejo no actúa si llegó un evento
    // más nuevo (presenceTouch difiere).
    const presenceTouch = new Map<string, number>();
    // Muchos contactos en modo privacidad (@lid 2024+) NUNCA emiten
    // 'unavailable' (WhatsApp lo bloquea), solo 'composing'/'available'.
    // Sin un TTL, "en línea" se quedaría PEGADO para siempre. 150s:
    // generoso para no parpadear durante interacción normal, pero corta
    // la mentira eterna si hay silencio total (no sabemos si sigue online).
    const PRESENCE_SAFETY_MS = 150_000;
    const presenceChannel = supabase
      .channel(`wa-presence:${tenantId}`)
      .on('broadcast', { event: 'presence' }, (payload) => {
        const { phone, status: rawStatus, last_seen } = payload.payload as { phone: string; status: string; last_seen?: string };
        // 'paused' = dejó de escribir pero sigue online (defensivo; el
        // backend ya normaliza). Solo 'unavailable' = realmente offline.
        const status = rawStatus === 'paused' ? 'available' : rawStatus;
        const online = status === 'composing' || status === 'recording' || status === 'available';
        const touchTs = Date.now();
        if (online) presenceTouch.set(phone, touchTs);
        else presenceTouch.delete(phone);
        setPresenceMap((prev) => {
          const next = new Map(prev);
          if (online) next.set(phone, status as 'composing' | 'recording' | 'available');
          else next.delete(phone); // 'unavailable' → offline
          return next;
        });
        // TTL de seguridad: si NO llega ningún evento nuevo en 150s
        // (presenceTouch sigue siendo touchTs), limpiamos "en línea" —
        // no podemos afirmar que siga online (WhatsApp no manda
        // 'unavailable' para privacy-restringidos). Resetea con c/evento.
        if (online) {
          setTimeout(() => {
            if (presenceTouch.get(phone) !== touchTs) return; // llegó algo más nuevo
            presenceTouch.delete(phone);
            setPresenceMap((prev) => {
              if (!prev.has(phone)) return prev;
              const next = new Map(prev);
              next.delete(phone);
              return next;
            });
          }, PRESENCE_SAFETY_MS);
        }
        // "Última conexión" derivada (Fase 1): refleja el last_seen en la
        // conv en vivo, sin esperar refetch. El backend solo lo envía en
        // transiciones online/offline (no en el flicker de "escribiendo").
        // Guard no-op CRÍTICO: si nada cambió, devolvemos la MISMA ref del
        // array → React hace bail-out y NO se dispara la cascada de
        // efectos `conversations`-dependientes (parpadeo del header).
        if (last_seen) {
          setConversations((prev) => {
            let changed = false;
            const next = prev.map((c) => {
              if (c.contact_phone === phone && c.contact_last_seen_at !== last_seen) {
                changed = true;
                return { ...c, contact_last_seen_at: last_seen };
              }
              return c;
            });
            return changed ? next : prev;
          });
        }
        // SOLO composing/recording tienen fallback (8s): si no llega el
        // 'paused'/'available' que los cierra, NO borramos (eso marcaría
        // offline falsamente) → revertimos a 'available' (sigue online).
        // 'available' NO tiene timer: es sticky hasta 'unavailable'.
        if (status === 'composing' || status === 'recording') {
          setTimeout(() => {
            setPresenceMap((prev) => {
              if (prev.get(phone) !== status) return prev; // ya cambió
              const next = new Map(prev);
              next.set(phone, 'available'); // dejó de escribir, sigue online
              return next;
            });
          }, 8000);
        }
      })
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
      void supabase.removeChannel(presenceChannel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);
}
